import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Sidebar from "@/components/Sidebar";
import NotificationBell from "@/components/NotificationBell";
import Dashboard from "@/pages/Dashboard";
import Servers from "@/pages/Servers";
import AIAssistant from "@/pages/AIAssistant";
import LogAnalyzer from "@/pages/LogAnalyzer";
import CommandCenter from "@/pages/CommandCenter";
import AppInstaller from "@/pages/AppInstaller";
import SecurityAudit from "@/pages/SecurityAudit";
import Approvals from "@/pages/Approvals";
import Login from "@/pages/Login";
import AuditLogs from "@/pages/AuditLogs";
import Settings from "@/pages/Settings";
import SecurityScans from "@/pages/SecurityScans";
import DirectTerminal from "@/pages/DirectTerminal";
import Runbooks from "@/pages/Runbooks";
import AgentTasks from "@/pages/AgentTasks";
import ITTickets from "@/pages/ITTickets";
import ADManagement from "@/pages/ADManagement";
import OdooAutoPilot from "@/pages/OdooAutoPilot";
import EventLogViewer from "@/pages/EventLogViewer";
import Infrastructure from "@/pages/Infrastructure";
import CloudDO from "@/pages/CloudDO";
import ModuleManager from "@/pages/ModuleManager";
import OdooAIModule from "@/pages/OdooAIModule";
import DeployApps from "@/pages/DeployApps";
import BackupsArchives from "@/pages/BackupsArchives";
import SecuritySetup from "@/pages/SecuritySetup";
import SecurityAutoPilot from "@/pages/SecurityAutoPilot";
import { StepUpGate } from "@/components/StepUpGate";

function enrollmentPending() {
  try {
    const t = localStorage.getItem("token") || "";
    return JSON.parse(atob(t.split(".")[1])).scope === "enrollment";
  } catch { return false; }
}

function AppContent() {
  const { user, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Limited enrollment session: force the security setup screen, nothing else.
  if (user && enrollmentPending()) {
    return <SecuritySetup />;
  }

  // If not logged in, show login page for /login route, otherwise redirect
  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <div className="flex">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <div className={`flex-1 min-h-screen flex flex-col transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-16'}`}>
        {/* Top Bar */}
        <header className="sticky top-0 z-40 flex items-center justify-end px-6 py-3 bg-[#09090b]/80 backdrop-blur-md border-b border-white/5" data-testid="top-bar">
          <NotificationBell />
        </header>
        <main className="flex-1">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/servers" element={<Servers />} />
            <Route path="/assistant" element={<AIAssistant />} />
            <Route path="/approvals" element={<Approvals />} />
            <Route path="/logs" element={<LogAnalyzer />} />
            <Route path="/commands" element={<CommandCenter />} />
            <Route path="/apps" element={<AppInstaller />} />
            <Route path="/security" element={<SecurityAudit />} />
            <Route path="/audit" element={<AuditLogs />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/scans" element={<SecurityScans />} />
            <Route path="/terminal" element={<DirectTerminal />} />
            <Route path="/runbooks" element={<Runbooks />} />
            <Route path="/tasks" element={<AgentTasks />} />
            <Route path="/helpdesk" element={<ITTickets />} />
            <Route path="/ad" element={<ADManagement />} />
            <Route path="/odoo" element={<OdooAutoPilot />} />
            <Route path="/event-logs" element={<EventLogViewer />} />
            <Route path="/infrastructure" element={<Infrastructure />} />
            <Route path="/cloud" element={<CloudDO />} />
            <Route path="/deploy" element={<DeployApps />} />
            <Route path="/backups" element={<BackupsArchives />} />
            <Route path="/autopilot-security" element={<SecurityAutoPilot />} />
            <Route path="/modules" element={<ModuleManager />} />
            <Route path="/odoo-ai" element={<OdooAIModule />} />
            <Route path="/login" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
      <StepUpGate />
    </div>
  );
}

function App() {
  return (
    <div className="min-h-screen bg-[#09090b]">
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
      <Toaster 
        position="bottom-right" 
        theme="dark"
        toastOptions={{
          style: {
            background: '#0c0c0e',
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#fff',
          },
        }}
      />
    </div>
  );
}

export default App;
